import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { TrendingUp, TrendingDown, RefreshCw, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { BACKEND_URL } from '../config';

const CryptoPage = () => {
  const navigate = useNavigate();
  const [cryptos, setCryptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchCryptoData = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await fetch(`${BACKEND_URL}/api/crypto/markets`);
      const data = await response.json();
      
      // Ensure data is always an array
      if (Array.isArray(data)) {
        setCryptos(data);
        setLastUpdate(new Date());
      } else {
        console.error('Invalid data format received:', data);
        setCryptos([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching crypto data:', error);
      setCryptos([]); // Set empty array on error
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load with loading spinner
    fetchCryptoData(true);
    
    // Auto-refresh every 30 seconds without loading spinner (to avoid rate limits)
    const interval = setInterval(() => {
      fetchCryptoData(false);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const filteredCryptos = cryptos.filter(crypto =>
    crypto.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    crypto.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatNumber = (num) => {
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatPrice = (price) => {
    if (price >= 1) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${price.toFixed(6)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-white mb-4">
            Cryptocurrency Market
          </h1>
          <p className="text-xl text-white/90">
            Real-time prices and market data for the top 20 cryptocurrencies
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Controls */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="w-full md:w-96 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search cryptocurrency..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                Last updated: {lastUpdate.toLocaleTimeString()}
              </div>
              <button
                onClick={() => fetchCryptoData(false)}
                className="flex items-center gap-2 px-4 py-2 bg-[#0d6571] text-white rounded-lg hover:bg-[#0d6571]/90 transition-colors"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Crypto Table */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#0d6571] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading cryptocurrency data...</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">#</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Name</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-700">Price</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-700">1h %</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-700">24h %</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-700">7d %</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-700">Market Cap</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-700">Volume (24h)</th>
                    <th className="text-center py-4 px-6 font-semibold text-gray-700">Last 7 Days</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCryptos.map((crypto, index) => (
                    <tr key={crypto.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-gray-600">{crypto.market_cap_rank}</td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <img src={crypto.image} alt={crypto.name} className="w-8 h-8" />
                          <div>
                            <div className="font-semibold text-gray-900">{crypto.name}</div>
                            <div className="text-sm text-gray-500 uppercase">{crypto.symbol}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right font-semibold text-gray-900">
                        {formatPrice(crypto.current_price)}
                      </td>
                      <td className={`py-4 px-6 text-right font-semibold ${
                        crypto.price_change_percentage_1h_in_currency >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        <div className="flex items-center justify-end gap-1">
                          {crypto.price_change_percentage_1h_in_currency >= 0 ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : (
                            <TrendingDown className="w-4 h-4" />
                          )}
                          {Math.abs(crypto.price_change_percentage_1h_in_currency || 0).toFixed(2)}%
                        </div>
                      </td>
                      <td className={`py-4 px-6 text-right font-semibold ${
                        crypto.price_change_percentage_24h >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        <div className="flex items-center justify-end gap-1">
                          {crypto.price_change_percentage_24h >= 0 ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : (
                            <TrendingDown className="w-4 h-4" />
                          )}
                          {Math.abs(crypto.price_change_percentage_24h || 0).toFixed(2)}%
                        </div>
                      </td>
                      <td className={`py-4 px-6 text-right font-semibold ${
                        crypto.price_change_percentage_7d_in_currency >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        <div className="flex items-center justify-end gap-1">
                          {crypto.price_change_percentage_7d_in_currency >= 0 ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : (
                            <TrendingDown className="w-4 h-4" />
                          )}
                          {Math.abs(crypto.price_change_percentage_7d_in_currency || 0).toFixed(2)}%
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right text-gray-900">
                        {formatNumber(crypto.market_cap)}
                      </td>
                      <td className="py-4 px-6 text-right text-gray-900">
                        {formatNumber(crypto.total_volume)}
                      </td>
                      <td className="py-4 px-6">
                        <div className="w-32 h-12 mx-auto">
                          <svg viewBox="0 0 164 48" className="w-full h-full">
                            <polyline
                              points={crypto.sparkline_in_7d.price
                                .map((price, i) => {
                                  const x = (i / (crypto.sparkline_in_7d.price.length - 1)) * 164;
                                  const minPrice = Math.min(...crypto.sparkline_in_7d.price);
                                  const maxPrice = Math.max(...crypto.sparkline_in_7d.price);
                                  const y = 48 - ((price - minPrice) / (maxPrice - minPrice)) * 48;
                                  return `${x},${y}`;
                                })
                                .join(' ')}
                              fill="none"
                              stroke={crypto.price_change_percentage_7d_in_currency >= 0 ? '#10b981' : '#ef4444'}
                              strokeWidth="2"
                            />
                          </svg>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredCryptos.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No cryptocurrencies found matching your search.</p>
              </div>
            )}
          </div>
        )}

        {/* Market Summary */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Market Information</h2>
          <p className="text-gray-600">
            Data provided by CoinGecko API. Prices update automatically every 30 seconds.
            Click the refresh button to manually update the data.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CryptoPage;
